import { isNil } from 'rambda';
import { useMemo } from 'react';
import usePeriodicValue from '@/hooks/use-periodic-value';
import { ConversationMessageContentType, type ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import { useAuthStore } from '@/stores/auth';

const isNotificationTypes = new Set([
  ConversationMessageContentType.Notice,
  ConversationMessageContentType.JoinGroup,
  ConversationMessageContentType.LeaveGroup,
  ConversationMessageContentType.AllBan,
]);

const cannotSummonContextMenuTypes = new Set([...isNotificationTypes, ConversationMessageContentType.Invite]);

export const cannotForwardMessageType = new Set([
  ...cannotSummonContextMenuTypes,
  ConversationMessageContentType.Call,
  ConversationMessageContentType.Contact,
]);

export default function useMessageInfo(message: Nullable<ConversationMessageTypeUnion>) {
  const userId = useAuthStore((s) => s.auth?.UserID);

  const isStale = usePeriodicValue(
    () => Boolean(message && isNil(message.Status) && message.CreateTime + 6_000 < Date.now()),
    isNil(message?.Status) ? 600 : null,
  );

  const info = useMemo(() => {
    if (!message)
      return {
        sessionType: 'session',
        sessionId: 0,
        isMine: false,
        isGroup: false,
        isPending: false,
        isNotification: false,
        canSummonContextMenu: false,
        isGroupAnnouncement: false,
        hasQuote: false,
      } as const;

    const sessionType = 'GroupID' in message ? 'group' : 'session';
    const sessionId = 'GroupID' in message ? message.GroupID : message.ChatSessionID;

    const hasQuote = Boolean(message.Quote);
    const isNotification = isNotificationTypes.has(message.MsgType);
    const isGroupAnnouncement = message.MsgType === ConversationMessageContentType.Notice && 'GroupInfo' in message;

    const canSummonContextMenu = !cannotSummonContextMenuTypes.has(message.MsgType);
    const canForward = !cannotForwardMessageType.has(message.MsgType) || Boolean(message.ReadDelete);

    return {
      sessionType,
      sessionId,
      isMine: userId === message.UserID,
      isGroup: 'GroupID' in message,
      isPending: message.Status === null,
      isNotification,
      canSummonContextMenu,
      canForward,
      isGroupAnnouncement,
      hasQuote,
    } as const;
  }, [message, userId]);

  return {
    ...info,
    isStale,
  };
}
